/**
 * @file textToSpeech.js
 * @description Text-to-speech functionality using Web Speech API
 */

class TextToSpeechService {
  constructor() {
    this.synth = window.speechSynthesis;
    this.voices = [];
    this.currentUtterance = null;
    this.isPlaying = false;
    this.isPaused = false;
    
    // Default settings
    this.settings = {
      rate: 1.0,
      pitch: 1.0,
      volume: 1.0,
      voice: null,
      lang: 'en-US'
    };

    this.loadVoices();
    
    // Load voices when they become available
    if (this.synth.onvoiceschanged !== undefined) {
      this.synth.onvoiceschanged = () => this.loadVoices();
    }
  }

  loadVoices() {
    this.voices = this.synth.getVoices();
    
    // Set default voice to English if available
    const defaultVoice = this.voices.find(voice => 
      voice.lang === 'en-US' || voice.lang.startsWith('en')
    );
    
    if (defaultVoice) {
      this.settings.voice = defaultVoice;
    }
  }

  getVoices() {
    return this.voices;
  }

  setVoice(voiceIndex) {
    if (this.voices[voiceIndex]) {
      this.settings.voice = this.voices[voiceIndex];
    }
  }

  setRate(rate) {
    this.settings.rate = Math.max(0.1, Math.min(10, rate));
  }

  setPitch(pitch) {
    this.settings.pitch = Math.max(0, Math.min(2, pitch));
  }

  setVolume(volume) {
    this.settings.volume = Math.max(0, Math.min(1, volume));
  }

  speak(text, options = {}) {
    if (!this.synth) {
      console.error('Speech synthesis not supported');
      return Promise.reject(new Error('Speech synthesis not supported'));
    }

    // Stop any current speech
    this.stop();

    return new Promise((resolve, reject) => {
      this.currentUtterance = new SpeechSynthesisUtterance(text);
      
      // Apply settings
      this.currentUtterance.rate = options.rate || this.settings.rate;
      this.currentUtterance.pitch = options.pitch || this.settings.pitch;
      this.currentUtterance.volume = options.volume || this.settings.volume;
      this.currentUtterance.voice = options.voice || this.settings.voice;
      this.currentUtterance.lang = options.lang || this.settings.lang;

      // Event handlers
      this.currentUtterance.onstart = () => {
        this.isPlaying = true;
        this.isPaused = false;
        if (options.onStart) options.onStart();
      };

      this.currentUtterance.onend = () => {
        this.isPlaying = false;
        this.isPaused = false;
        this.currentUtterance = null;
        if (options.onEnd) options.onEnd();
        resolve();
      };

      this.currentUtterance.onerror = (event) => {
        this.isPlaying = false;
        this.isPaused = false;
        this.currentUtterance = null;
        if (options.onError) options.onError(event);
        reject(event);
      };

      this.currentUtterance.onpause = () => {
        this.isPaused = true;
        if (options.onPause) options.onPause();
      };

      this.currentUtterance.onresume = () => {
        this.isPaused = false;
        if (options.onResume) options.onResume();
      };

      // Start speaking
      this.synth.speak(this.currentUtterance);
    });
  }

  pause() {
    if (this.synth && this.isPlaying && !this.isPaused) {
      this.synth.pause();
    }
  }

  resume() {
    if (this.synth && this.isPlaying && this.isPaused) {
      this.synth.resume();
    }
  }

  stop() {
    if (this.synth) {
      this.synth.cancel();
      this.isPlaying = false;
      this.isPaused = false;
      this.currentUtterance = null;
    }
  }

  toggle() {
    if (this.isPlaying) {
      if (this.isPaused) {
        this.resume();
      } else {
        this.pause();
      }
    }
  }

  getStatus() {
    return {
      isPlaying: this.isPlaying,
      isPaused: this.isPaused,
      isSupported: !!this.synth
    };
  }

  // Utility method to split long text into chunks for better speech synthesis
  splitTextIntoChunks(text, maxLength = 200) {
    const sentences = text.split(/[.!?]+/).filter(s => s.trim().length > 0);
    const chunks = [];
    let currentChunk = '';

    for (const sentence of sentences) {
      if (currentChunk.length + sentence.length > maxLength && currentChunk.length > 0) {
        chunks.push(currentChunk.trim());
        currentChunk = sentence;
      } else {
        currentChunk += (currentChunk ? '. ' : '') + sentence;
      }
    }

    if (currentChunk.trim().length > 0) {
      chunks.push(currentChunk.trim());
    }

    return chunks;
  }

  // Speak long text by breaking it into chunks
  async speakLongText(text, options = {}) {
    const chunks = this.splitTextIntoChunks(text);
    
    for (const chunk of chunks) {
      try {
        await this.speak(chunk, options);
        // Small pause between chunks
        await new Promise(resolve => setTimeout(resolve, 100));
      } catch (error) {
        console.error('Error speaking chunk:', error);
        break;
      }
    }
  }
}

// Create a singleton instance
export const ttsService = new TextToSpeechService();

// Export utility functions
export const {
  speak,
  pause,
  resume,
  stop,
  toggle,
  getStatus,
  getVoices,
  setVoice,
  setRate,
  setPitch,
  setVolume,
  speakLongText
} = ttsService;
